/**
 * Global Axios instance with automatic auth headers and token refresh
 *
 * Usage: import apiClient from '@/lib/axiosInstance';
 * Then use apiClient.get(), apiClient.post(), etc. instead of raw axios.
 * Auth headers are injected automatically.
 */

import axios from 'axios';
import { getBackendUrl } from './api';
import { clearUserRole } from './userRole';
import { clearSessionData, getAccessToken, getRefreshToken, storeTokens } from './session';

const BACKEND_URL = getBackendUrl();

const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
});

// Endpoints that require extended timeouts (long-running scans, pentests)
const LONG_RUNNING_ENDPOINTS = [
  '/email-security/scan-domain',
  '/email-security/pentest',
  '/email-security/assessment',
];
const LONG_TIMEOUT = 120000; // 2 minutes

// In-flight GET dedupe: si N componentes piden el mismo endpoint
// dentro de la misma ventana, todos comparten la misma promise.
// Evita 429 cascading cuando Layout/Navigation/page hacen el mismo fetch en mount.
const inFlight = new Map();
const DEDUPE_BLACKLIST = ['/auth/refresh', '/auth/login', '/auth/logout'];

// Endpoints donde un 401 NO significa "token caducado" y por tanto NO se
// refresca (FE-11). Un inicio de sesión con la contraseña mal devuelve 401: si
// eso entrara en el refresco, un fallo de contraseña borraría el almacenamiento
// de sesión y gastaría una petición a /auth/refresh para nada. El 401 de estos
// endpoints es la respuesta, no un accidente: se devuelve tal cual a quien llama.
const NO_REFRESH_ON_401 = ['/auth/login', '/auth/register', '/auth/refresh'];

function dedupeKey(config) {
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${config.method || 'get'}:${config.url}:${params}`;
}

// Request interceptor: auto-add Authorization header + dynamic timeout
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Apply extended timeout for long-running endpoints
    const url = config.url || '';
    if (LONG_RUNNING_ENDPOINTS.some(ep => url.includes(ep))) {
      config.timeout = LONG_TIMEOUT;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Wrap GET with dedupe (only GET, no body methods)
const originalGet = apiClient.get.bind(apiClient);
apiClient.get = function dedupedGet(url, config = {}) {
  if (DEDUPE_BLACKLIST.some(p => url.includes(p)) || config.skipDedupe) {
    return originalGet(url, config);
  }
  const key = dedupeKey({ method: 'get', url, params: config.params });
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }
  const promise = originalGet(url, config).finally(() => {
    // Liberar la entrada un instante despues de resolver
    setTimeout(() => inFlight.delete(key), 50);
  });
  inFlight.set(key, promise);
  return promise;
};

// Response interceptor: handle 401 with token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Max automatic retries for 429 (rate limit) responses before giving up.
const MAX_RATELIMIT_RETRIES = 2;
const DEFAULT_RETRY_AFTER_MS = 2000;

function parseRetryAfterMs(error) {
  // Prefer the server-provided hints (Retry-After header or JSON retry_after).
  const header = error.response?.headers?.['retry-after'];
  const bodyRetry = error.response?.data?.retry_after;
  const seconds = Number(header ?? bodyRetry);
  if (Number.isFinite(seconds) && seconds > 0) {
    // Cap to avoid hanging the UI too long on a misbehaving backend.
    return Math.min(seconds * 1000, 8000);
  }
  return DEFAULT_RETRY_AFTER_MS;
}

// ÚNICO camino de refresco de token de toda la aplicación (FE-11).
//
// Antes había dos implementaciones: esta y otra copiada dentro de
// `AuthContext.refreshToken` con `fetch` crudo. Dos caminos significan dos
// criterios sobre cuándo se cierra la sesión y dos sitios donde escribir
// `access_token`: con la pestaña en segundo plano podían pisarse el token.
// Ahora `AuthContext` llama a ESTA función y el interceptor de abajo también.
//
// Usa `axios` pelado a propósito, no `apiClient`: si el refresco fuese por
// `apiClient` y devolviera 401, volvería a entrar en el interceptor de abajo
// y se refrescaría en bucle.
//
// Devuelve un estado, no solo el token, porque quien llama necesita distinguir
// "la sesión ha terminado" de "el backend no contesta ahora mismo":
//   - { status: 'ok', token }        -> renovado
//   - { status: 'expired' }          -> el servidor rechaza el refresh token
//                                      (o no hay): la sesión ha terminado
//   - { status: 'unavailable' }      -> 5xx/502 de un despliegue o red caída:
//                                      la respuesta NO dice nada de la sesión
// Traduce el fallo de /auth/refresh a uno de los estados de arriba. El error
// NO se descarta: se le entrega a esta función, que es quien decide qué
// significa, y viaja en `error` para que quien llama pueda enseñarlo.
function classifyRefreshFailure(err) {
  const code = err?.response?.status;
  if (code === 401 || code === 403) {
    return { status: 'expired', token: null, error: err };
  }
  return { status: 'unavailable', token: null, error: err };
}

export async function refreshAccessToken() {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    return { status: 'expired', token: null };
  }

  try {
    const response = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {
      refresh_token: refreshTokenValue,
    });

    const token = response.data.access_token;
    // Un solo sitio escribe los tokens: lib/session.
    storeTokens({ accessToken: token, refreshToken: response.data.refresh_token });
    return { status: 'ok', token };
  } catch (err) {
    return classifyRefreshFailure(err);
  }
}

// Una ESCRITURA invalida el dedupe de GET.
//
// El dedupe de arriba guarda la promise de cada GET y la reutiliza durante los
// 50 ms siguientes a que resuelva. Eso es bueno para el aluvión de peticiones
// del montaje, pero convertía en mentira cualquier recarga posterior a una
// escritura: al borrar un conector, `removeConnector` hacía DELETE (200) y
// llamaba a `loadData()`; el `GET /siem/connectors` de esa recarga caía dentro
// de la ventana de 50 ms del GET ANTERIOR (el que lanzó "Probar conexión"), así
// que axios devolvía la promise YA RESUELTA con la lista de ANTES del borrado.
// No salía ni una petición a la red —en la traza de Playwright no aparece nada—
// y la tarjeta seguía pintando un conector que ya no existía.
//
// Tras cualquier método que no sea GET, lo cacheado está obsoleto por
// definición: se tira. Como mucho cuesta un GET de más.
function invalidateGetDedupe(config) {
  if ((config?.method || 'get').toLowerCase() !== 'get') {
    inFlight.clear();
  }
}

apiClient.interceptors.response.use(
  (response) => {
    invalidateGetDedupe(response.config);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    invalidateGetDedupe(originalRequest);

    // 429 (rate limit) — NEVER treat as auth failure. Do NOT clear the token
    // or redirect to /login. Degrade gracefully: retry with backoff honoring
    // Retry-After. Critical for GET /auth/me, where a 429 used to bubble up
    // as "not authenticated" and log the user out.
    if (error.response?.status === 429 && originalRequest) {
      originalRequest._rateLimitRetries = originalRequest._rateLimitRetries || 0;
      if (originalRequest._rateLimitRetries < MAX_RATELIMIT_RETRIES) {
        originalRequest._rateLimitRetries += 1;
        const delay = parseRetryAfterMs(error);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return apiClient(originalRequest);
      }
      // Out of retries: reject WITHOUT logging out so the caller can show a
      // soft "slow down" message instead of bouncing to /login.
      return Promise.reject(error);
    }

    // Un 401 de login/registro/refresco es la respuesta del servidor, no una
    // sesión caducada: no se refresca ni se manda a /login.
    if (
      error.response?.status === 401 &&
      NO_REFRESH_ON_401.some((p) => (originalRequest?.url || '').includes(p))
    ) {
      return Promise.reject(error);
    }

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const result = await refreshAccessToken();

        if (result.status !== 'ok') {
          // Aquí SÍ se manda a la pantalla de inicio de sesión: a diferencia de
          // `AuthContext`, que solo comprueba la sesión al arrancar, aquí hay
          // una petición concreta del usuario que acaba de recibir un 401 y no
          // se ha podido reintentar. Dejarle en una pantalla muda sería peor.
          processQueue(result.error || new Error(`Token refresh failed: ${result.status}`));
          redirectToLogin();
          return Promise.reject(error);
        }

        processQueue(null, result.token);
        originalRequest.headers.Authorization = `Bearer ${result.token}`;
        return apiClient(originalRequest);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function redirectToLogin() {
  clearSessionData();
  clearUserRole();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

export default apiClient;
